import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import type { LandingNavItem } from '../../../../shared/types/people-landing';
import { deaccentRaw } from '../../../../shared/text-normalize';

const HOME_SLUG = 'home';

function slugify(title: string): string {
  return deaccentRaw(title)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
}

interface PagesManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nav: LandingNavItem[];
  /** Slug de la página que se está viendo — para navegar solo si se borra ESA. */
  activeSlug?: string;
}

export function PagesManagerDialog({ open, onOpenChange, nav, activeSlug }: PagesManagerDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [pages, setPages] = useState<LandingNavItem[]>(nav);
  const [newTitle, setNewTitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LandingNavItem | null>(null);

  useEffect(() => {
    if (open) setPages(nav);
  }, [open, nav]);

  const invalidate = () => {
    utils.peopleLanding.getNav.invalidate();
    utils.peopleLanding.getPage.invalidate();
  };

  const saveNavMutation = trpc.peopleLanding.saveNav.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(t('people_landing.pages.saved', 'Navegación guardada'));
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const createMutation = trpc.peopleLanding.createPage.useMutation({
    onSuccess: (_d, vars) => {
      invalidate();
      toast.success(t('people_landing.pages.created', 'Página creada'));
      setNewTitle('');
      onOpenChange(false);
      navigate(`/people/landing/${vars.slug}`);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.peopleLanding.deletePage.useMutation({
    onSuccess: (_d, vars) => {
      invalidate();
      toast.success(t('people_landing.pages.deleted', 'Página eliminada'));
      setDeleteTarget(null);
      // solo salir de la página si se borró la que se estaba viendo
      if (vars.slug === activeSlug) navigate('/people/landing');
    },
    onError: (e) => toast.error(e.message),
  });

  const move = (idx: number, dir: -1 | 1) => {
    setPages((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleCreate = () => {
    const title = newTitle.trim();
    if (!title) return;
    const slug = slugify(title);
    if (!slug) {
      toast.error(t('people_landing.pages.invalid_title', 'El título no genera un slug válido'));
      return;
    }
    if (pages.some((p) => p.slug === slug)) {
      toast.error(t('people_landing.pages.duplicate', 'Ya existe una página con ese nombre'));
      return;
    }
    createMutation.mutate({ slug, title });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('people_landing.pages.title', 'Administrar páginas')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            {pages.map((page, idx) => (
              <div key={page.slug} className="flex items-center gap-2">
                <Input
                  value={page.title}
                  onChange={(e) =>
                    setPages((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, title: e.target.value } : p))
                    )
                  }
                />
                <span className="text-xs text-muted-foreground font-mono shrink-0 w-28 truncate">
                  /{page.slug}
                </span>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(idx, 1)} disabled={idx === pages.length - 1}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive disabled:opacity-30"
                    disabled={page.slug === HOME_SLUG}
                    onClick={() => setDeleteTarget(page)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t pt-3">
            <Input
              placeholder={t('people_landing.pages.new_placeholder', 'Título de la página nueva')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Button
              variant="outline"
              onClick={handleCreate}
              disabled={createMutation.isPending || !newTitle.trim()}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-1.5 h-4 w-4" />
              )}
              {!createMutation.isPending && t('people_landing.pages.create', 'Crear')}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.ui.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={() =>
                saveNavMutation.mutate({
                  pages: pages.map((p) => ({ slug: p.slug, title: p.title.trim() || p.slug })),
                })
              }
              disabled={saveNavMutation.isPending}
            >
              {saveNavMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.ui.save', 'Guardar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('people_landing.pages.delete_title', '¿Eliminar página?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'people_landing.pages.delete_desc',
                'Se elimina "{{title}}" con todo su contenido. Esta acción no se puede deshacer.',
                { title: deleteTarget?.title }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate({ slug: deleteTarget.slug })}
            >
              {t('common.ui.delete', 'Eliminar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
